import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { RAMS_TEMPLATES, type RamsTemplate } from '../data/ramsTemplates';
import { useUserOrg } from '../lib/org';
import { ramsTradesForOrg } from '../lib/features';

// localStorage keys for the Pinned + Recent feature. Org-scoped via
// the org id we already have in context — pinned RAMS for an electrician
// org are not relevant to a bricklayer org if the user ever switches.
function pinnedKey(orgId: string | undefined): string {
  return `rams.pinned:${orgId ?? 'anon'}`;
}
function recentKey(orgId: string | undefined): string {
  return `rams.recent:${orgId ?? 'anon'}`;
}

const RECENT_LIMIT = 3;

function loadIds(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveIds(key: string, ids: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // localStorage full / private mode — silently ignore. Pinned/Recent
    // are nice-to-have, not load-bearing.
  }
}

export function RamsPresets() {
  const { siteId } = useParams<{ siteId: string }>();
  const { org } = useUserOrg();
  const [query, setQuery] = useState('');
  const [activeTrade, setActiveTrade] = useState<string | null>(null);
  const [showForYouOnly, setShowForYouOnly] = useState(false);

  // Pinned + Recent state, hydrated from localStorage on mount. Pinned
  // is a Set of template ids the user starred; Recent is an ordered list
  // (most-recent first) of templates they opened.
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(new Set());
  const [recentIds, setRecentIds] = useState<string[]>([]);

  useEffect(() => {
    setPinnedIds(new Set(loadIds(pinnedKey(org?.org_id))));
    setRecentIds(loadIds(recentKey(org?.org_id)));
  }, [org?.org_id]);

  function togglePin(id: string) {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveIds(pinnedKey(org?.org_id), Array.from(next));
      return next;
    });
  }

  function markRecent(id: string) {
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, RECENT_LIMIT);
      saveIds(recentKey(org?.org_id), next);
      return next;
    });
  }

  const pinnedTemplates = useMemo(
    () => RAMS_TEMPLATES.filter((t) => pinnedIds.has(t.id)),
    [pinnedIds],
  );
  const recentTemplates = useMemo(
    () => recentIds.map((id) => RAMS_TEMPLATES.find((t) => t.id === id)).filter((t): t is RamsTemplate => !!t),
    [recentIds],
  );

  // Templates relevant to the org's profile (trades + risky activities)
  const forYouTrades = useMemo(() => {
    if (!org) return [];
    return ramsTradesForOrg(org.trades, org.risky_activities);
  }, [org]);
  const forYouTemplates = useMemo(() => {
    if (forYouTrades.length === 0) return [];
    return RAMS_TEMPLATES.filter((t) => t.trades.some((tr) => forYouTrades.includes(tr)));
  }, [forYouTrades]);

  // Build the set of all unique trades across templates
  const allTrades = useMemo(() => {
    const s = new Set<string>();
    for (const t of RAMS_TEMPLATES) for (const tr of t.trades) s.add(tr);
    return Array.from(s).sort();
  }, []);

  // Filter templates by trade + query (+ optional 'for you' filter)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = showForYouOnly ? forYouTemplates : RAMS_TEMPLATES;
    return base.filter((t) => {
      const matchesTrade = activeTrade ? t.trades.includes(activeTrade) : true;
      if (!matchesTrade) return false;
      if (!q) return true;
      const haystack = (t.title + ' ' + t.description + ' ' + t.trades.join(' ')).toLowerCase();
      return haystack.includes(q);
    });
  }, [query, activeTrade, showForYouOnly, forYouTemplates]);

  // Group results by primary trade (only when nothing filtered)
  const grouped: Record<string, RamsTemplate[]> = {};
  if (!activeTrade && !query.trim()) {
    for (const t of RAMS_TEMPLATES) {
      const primary = t.trades[0] ?? 'Other';
      (grouped[primary] = grouped[primary] ?? []).push(t);
    }
  }
  const groupedTrades = Object.keys(grouped).sort();

  return (
    <AppShell title="Pick a RAMS" back={`/sites/${siteId ?? ''}/rams`}>
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: '0 0 6px', color: 'var(--ink-soft)' }}>
          <strong>The hard work is already done.</strong> Pick the one closest to your job,
          read every section, and tweak anything that isn&apos;t right for <em>your</em> site.
        </p>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
          Browse by trade or search below. These are starting points — <strong>not</strong> finished documents.
        </p>
      </div>

      <div className="rams-search-row">
        <input
          type="text"
          className="rams-search-input"
          placeholder="🔍 Search title, description, trade…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {forYouTemplates.length > 0 && (
        <div className="rams-trade-chips" style={{ marginBottom: 4 }}>
          <button type="button"
            className={`rams-trade-chip${showForYouOnly ? ' active' : ''}`}
            onClick={() => { setShowForYouOnly((v) => !v); setActiveTrade(null); }}>
            ★ For your trade ({forYouTemplates.length})
          </button>
          {!showForYouOnly && <span style={{ fontSize: 12, color: 'var(--ink-soft)', alignSelf: 'center', marginLeft: 4 }}>or browse all below</span>}
        </div>
      )}

      <div className="rams-trade-chips" role="tablist" aria-label="Filter by trade">
        <button
          type="button"
          className={`rams-trade-chip ${activeTrade === null ? 'active' : ''}`}
          onClick={() => setActiveTrade(null)}
        >
          All trades
        </button>
        {allTrades.map((t) => (
          <button
            key={t}
            type="button"
            className={`rams-trade-chip ${activeTrade === t ? 'active' : ''}`}
            onClick={() => setActiveTrade(activeTrade === t ? null : t)}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Pinned + Recent shortcuts — only shown in the default view (no
          query, no trade filter). Pinned is the star-toggle favourites;
          Recent is the last few templates the user opened. Both speed up
          the "I do brickwork every week, I don't need to re-search"
          case which is most of the actual user base. Templates also
          stay in their normal section below so browsing isn't broken. */}
      {!activeTrade && !query.trim() && !showForYouOnly && pinnedTemplates.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <h3 className="rams-company-h">★ Pinned</h3>
          <ul className="rams-preset-list">
            {pinnedTemplates.map((t) => (
              <li key={`pinned-${t.id}`}>
                <PresetRow t={t} siteId={siteId ?? ''} pinned onTogglePin={togglePin} onOpen={markRecent} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {!activeTrade && !query.trim() && !showForYouOnly && recentTemplates.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <h3 className="rams-company-h">Recent</h3>
          <ul className="rams-preset-list">
            {recentTemplates.map((t) => (
              <li key={`recent-${t.id}`}>
                <PresetRow
                  t={t}
                  siteId={siteId ?? ''}
                  pinned={pinnedIds.has(t.id)}
                  onTogglePin={togglePin}
                  onOpen={markRecent}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', marginTop: 12 }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No drafts match. Try clearing the search or picking a different trade.
          </p>
        </div>
      ) : !activeTrade && !query.trim() ? (
        // Default view — grouped by primary trade
        <>
          {groupedTrades.map((trade) => (
            <div key={trade} style={{ marginBottom: 18 }}>
              <h3 className="rams-company-h">{trade}</h3>
              <ul className="rams-preset-list">
                {grouped[trade].map((t) => (
                  <li key={t.id}>
                    <PresetRow
                      t={t}
                      siteId={siteId ?? ''}
                      pinned={pinnedIds.has(t.id)}
                      onTogglePin={togglePin}
                      onOpen={markRecent}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      ) : (
        // Filtered view — flat list
        <ul className="rams-preset-list" style={{ marginTop: 12 }}>
          {filtered.map((t) => (
            <li key={t.id}>
              <PresetRow
                t={t}
                siteId={siteId}
                pinned={pinnedIds.has(t.id)}
                onTogglePin={togglePin}
                onOpen={markRecent}
              />
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}

interface PresetRowProps {
  t: RamsTemplate;
  siteId: string | undefined;
  pinned: boolean;
  onTogglePin: (id: string) => void;
  onOpen: (id: string) => void;
}

function PresetRow({ t, siteId, pinned, onTogglePin, onOpen }: PresetRowProps) {
  return (
    <div style={{ position: 'relative' }}>
      <Link
        to={`/sites/${siteId}/rams/preset/${t.id}`}
        className="rams-preset-row"
        onClick={() => onOpen(t.id)}
      >
        <div className="rams-preset-text">
          <p className="rams-preset-title">{t.title}</p>
          <p className="rams-preset-trade">{t.trades.join(' · ')}</p>
          <p className="rams-preset-desc">{t.description}</p>
          <div className="rams-hazards" style={{ marginTop: 8 }}>
            {t.defaultHazards.slice(0, 4).map((h) => (
              <span key={h} className="rams-hazard-chip">{h}</span>
            ))}
          </div>
        </div>
        <span className="chev">›</span>
      </Link>
      {/* Star button sits on top of the row, absolutely positioned so it
          doesn't interfere with the Link's tap area. stopPropagation +
          preventDefault keep the Link from navigating when the user
          just wants to pin/unpin. */}
      <button
        type="button"
        aria-label={pinned ? `Unpin ${t.title}` : `Pin ${t.title}`}
        aria-pressed={pinned}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onTogglePin(t.id);
        }}
        style={{
          position: 'absolute',
          top: 8,
          right: 32,
          width: 32,
          height: 32,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          color: pinned ? 'var(--warn, #BF8E3D)' : 'var(--ink-soft)',
          padding: 0,
        }}
      >
        {pinned ? '★' : '☆'}
      </button>
    </div>
  );
}
